import { Interface } from 'ethers'
import { PublicClient } from 'viem'

import { ChainId } from '@src/configs/chainInfo'
import { GasFeeSimulationResults } from '@src/types/gasFees'
import { GasFeeMetrics } from '@src/types/gasFees'
import { ContractWriteParams } from '@src/types/gasFees'
import { simulateEVMGasFees } from '@src/utils/evm/simulateGasFees'
import { simulateSolanaGasFees } from '@src/utils/solana/simulateGasFees'
import { simulateTronGasFees } from '@src/utils/tron/simulateGasFees'

const PRINT_GAS_ERRORS = false

export function getInterface(signature: string) {
    // Prepend 'function '
    const hrAbi = [`function ${signature}`]

    // Create an Interface
    const iface = new Interface(hrAbi)

    // This returns an array of fragments
    // (in v6, iface.fragments is a read-only array of Fragment objects)
    return iface
}

export const simulateGasFees = async (
    transactions: ContractWriteParams[],
    gasFeeMetrics: GasFeeMetrics,
    publicClient?: PublicClient
): Promise<GasFeeSimulationResults> => {
    return new Promise<GasFeeSimulationResults>((resolve, reject) => {
        ;(async () => {
            try {
                const uniqueChainIds = new Set(transactions.map(tx => tx.chainId))
                if (uniqueChainIds.size > 1) {
                    throw new Error(
                        `All transactions must have the same chainId. Found: ${Array.from(uniqueChainIds).join(', ')}`
                    )
                }

                const chainId = transactions[0].chainId

                if (chainId === ChainId.Tron) {
                    resolve(await simulateTronGasFees(transactions, gasFeeMetrics))
                } else if (chainId === ChainId.Solana) {
                    resolve(await simulateSolanaGasFees(transactions, gasFeeMetrics))
                } else {
                    if (!publicClient) {
                        throw new Error('Client unavailable')
                    }
                    resolve(await simulateEVMGasFees(transactions, gasFeeMetrics, publicClient))
                }
            } catch (err) {
                PRINT_GAS_ERRORS && console.error(err)
                reject(err)
            }
        })()
    })
}

/**
 ETH:
 {
    "baseFee":     "4372564494n",
    "priorityFee": "165000n",
    "maxFee":      "8745293988n"
  }

  ARB:
  {
    "baseFee":     "11000000n",
    "priorityFee": "0n",
    "maxFee":      "22000000n"
   }

   Base: 
   {
    "baseFee":     "12912139n",
    "priorityFee": "1650000n",
    "maxFee":      "27474279n"
    }
	
	Polygon:
	{
    "baseFee":     "2787864689n",
    "priorityFee": "45610197442n",
    "maxFee":      "51185926821n"
}
 */
